import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { recordFeltRecent } from "@/lib/felt-storage";
import { SaveToProject } from "@/components/felt/SaveToProject";
import { ChevronLeft } from "lucide-react";
import {
  FieldCard,
  FieldNumber,
  FieldSelect,
  StatusRow,
  parseNum,
} from "@/components/felt/FieldInput";
import {
  voltageDrop,
  maxCableLength,
  type Conductor,
  type Phase,
  type ResistivityMethod,
} from "@/domain/calculators";

export const Route = createFileRoute("/felt/kurs")({
  head: () => ({
    meta: [
      { title: "Grovsjekk kurs | VERQO felt" },
      {
        name: "description",
        content:
          "Grovsjekk av kurs på byggeplass: spenningsfall, Ib ≤ In ≤ Iz og maks kabellengde ved 4 % på én skjerm.",
      },
      { property: "og:title", content: "Grovsjekk kurs" },
      {
        property: "og:description",
        content: "Spenningsfall, vernkontroll og maks kabellengde på én skjerm.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
    ],
  }),
  component: FeltKurs,
});

const METHOD_LABEL: Record<ResistivityMethod, string> = {
  strict: "Strict – konservativ (Store Blå)",
  iec: "IEC 60364-5-52 Annex G",
  nelfo: "Nelfo lærebok (ρ₂₀ × 1,2)",
  physical: "Fysisk (ρ₂₀ + α, 70 °C)",
};

function fmt(n: number, d = 2) {
  return n.toLocaleString("nb-NO", { minimumFractionDigits: d, maximumFractionDigits: d });
}

function FeltKurs() {
  const [breaker, setBreaker] = useState("16");
  const [cs, setCs] = useState("2,5");
  const [length, setLength] = useState("");
  const [load, setLoad] = useState("");
  const [voltage, setVoltage] = useState("230");
  const [phase, setPhase] = useState<"1" | "3">("1");
  const [material, setMaterial] = useState<Conductor>("cu");
  const [iz, setIz] = useState("");
  const [method, setMethod] = useState<ResistivityMethod>("strict");

  const result = useMemo(() => {
    const In = parseNum(breaker);
    const A = parseNum(cs);
    const L = parseNum(length);
    const Ib = parseNum(load);
    const U = parseNum(voltage);
    const Iz = parseNum(iz);
    if (In === null || A === null || L === null || Ib === null || U === null) return null;

    try {
      const drop = voltageDrop({
        phase: Number(phase) as Phase,
        length: L,
        current: Ib,
        crossSection: A,
        voltage: U,
        material,
        method,
      });
      const max = maxCableLength({
        phase: Number(phase) as Phase,
        current_A: Ib,
        crossSection_mm2: A,
        voltage_V: U,
        maxDropPercent: 4,
        material: material === "cu" ? "copper" : "aluminium",
        method,
      });
      return { drop, max, In, Ib, Iz };
    } catch (e) {
      return { error: (e as Error).message };
    }
  }, [breaker, cs, length, load, voltage, phase, material, iz, method]);

  useEffect(() => {
    if (!result || "error" in result) return;
    const t = window.setTimeout(() => {
      recordFeltRecent({
        to: "/felt/kurs",
        title: "Grovsjekk kurs",
        summary: `${fmt(result.In, 0)} A · ${cs} mm² · ${length} m · ΔU ${fmt(result.drop.dropPercent)} %`,
      });
    }, 800);
    return () => window.clearTimeout(t);
  }, [result, cs, length]);


  return (
    <>
      <Link
        to="/felt"
        className="mb-3 inline-flex h-12 items-center text-sm text-muted-foreground"
      >
        <ChevronLeft className="h-5 w-5" /> Feltmodus
      </Link>
      <h1 className="mb-4 text-2xl font-bold tracking-tight">Grovsjekk kurs</h1>

      <div className="space-y-4">
        <FieldCard title="Kurs">
          <div className="grid grid-cols-2 gap-3">
            <FieldNumber label="Vern (In)" unit="A" value={breaker} onChange={setBreaker} />
            <FieldNumber label="Belastning (Ib)" unit="A" value={load} onChange={setLoad} />
            <FieldNumber label="Tverrsnitt" unit="mm²" value={cs} onChange={setCs} />
            <FieldNumber label="Kabellengde" unit="m" value={length} onChange={setLength} />
            <FieldNumber label="Spenning" unit="V" value={voltage} onChange={setVoltage} />
            <FieldSelect
              label="Fasetall"
              value={phase}
              onChange={setPhase}
              options={[
                { value: "1", label: "1-fase" },
                { value: "3", label: "3-fase" },
              ]}
            />
            <FieldSelect
              label="Materiale"
              value={material}
              onChange={setMaterial}
              options={[
                { value: "cu", label: "Kobber" },
                { value: "al", label: "Aluminium" },
              ]}
            />
            <FieldNumber label="Iz (korrigert)" unit="A" value={iz} onChange={setIz} />
          </div>
          <div className="mt-3">
            <FieldSelect
              label="Beregningsmetode"
              value={method}
              onChange={setMethod}
              options={(Object.keys(METHOD_LABEL) as ResistivityMethod[]).map((m) => ({
                value: m,
                label: METHOD_LABEL[m],
              }))}
            />
          </div>
        </FieldCard>

        {result && "error" in result && (
          <div className="card p-4 text-sm text-rose-600">{result.error}</div>
        )}

        {result && !("error" in result) && (
          <>
            <FieldCard title="Spenningsfall">
              <div className="space-y-2">
                <StatusRow label="ΔU" value={`${fmt(result.drop.dropVolt)} V`} />
                <StatusRow
                  label="ΔU ≤ 3 % (lys)"
                  value={`${fmt(result.drop.dropPercent)} %`}
                  ok={result.drop.withinLighting}
                />
                <StatusRow
                  label="ΔU ≤ 5 % (øvrig)"
                  value={`${fmt(result.drop.dropPercent)} %`}
                  ok={result.drop.withinGeneral}
                />
              </div>
            </FieldCard>

            <FieldCard title="Ib ≤ In ≤ Iz">
              <div className="space-y-2">
                <StatusRow
                  label={`Ib ≤ In (${fmt(result.Ib, 1)} ≤ ${fmt(result.In, 1)} A)`}
                  value={result.Ib <= result.In ? "OK" : "For høy last"}
                  ok={result.Ib <= result.In}
                />
                {result.Iz === null ? (
                  <StatusRow label="In ≤ Iz" value="Iz ikke oppgitt" />
                ) : (
                  <StatusRow
                    label={`In ≤ Iz (${fmt(result.In, 1)} ≤ ${fmt(result.Iz, 1)} A)`}
                    value={result.In <= result.Iz ? "OK" : "Kabel for svak"}
                    ok={result.In <= result.Iz}
                  />
                )}
              </div>
            </FieldCard>

            <FieldCard title="Maks kabellengde ved 4 %">
              <div className="space-y-2">
                <StatusRow
                  label="Maks lengde"
                  value={`${fmt(result.max.maxLength_m, 1)} m`}
                />
                <StatusRow
                  label="Valgt lengde innenfor"
                  value={`${fmt(parseNum(length) ?? 0, 1)} m`}
                  ok={(parseNum(length) ?? 0) <= result.max.maxLength_m}
                />
              </div>
            </FieldCard>

            <p className="rounded-lg border border-border bg-muted/40 px-3 py-2 text-sm">
              <span className="font-semibold">Metode:</span> {METHOD_LABEL[method]} · ρ ={" "}
              {fmt(result.drop.resistivity, 4)} Ω·mm²/m
            </p>
            <p className="text-xs text-muted-foreground">
              Veiledende grovsjekk. Endelig dimensjonering og vern må kontrolleres mot
              gjeldende regelverk.
            </p>

            <SaveToProject
              result={{
                title: "Grovsjekk kurs",
                lines: [
                  `Vern ${fmt(result.In, 0)} A · Ib ${fmt(result.Ib, 1)} A · ${cs} mm² · ${length} m · ${voltage} V · ${phase}-fase · ${material === "cu" ? "kobber" : "aluminium"}`,
                  `ΔU ${fmt(result.drop.dropVolt)} V (${fmt(result.drop.dropPercent)} %)`,
                  `Maks lengde ved 4 %: ${fmt(result.max.maxLength_m, 1)} m`,
                  `Metode: ${METHOD_LABEL[method]} · ρ = ${fmt(result.drop.resistivity, 4)} Ω·mm²/m`,
                ],
                meta: {
                  calculator: "felt_grovsjekk_kurs",
                  breaker_A: result.In,
                  load_A: result.Ib,
                  crossSection_mm2: parseNum(cs),
                  length_m: parseNum(length),
                  voltage_V: parseNum(voltage),
                  phase: Number(phase),
                  material,
                  iz_A: result.Iz,
                  method,
                  dropVolt: result.drop.dropVolt,
                  dropPercent: result.drop.dropPercent,
                  maxLength_m: result.max.maxLength_m,
                },
              }}
            />
          </>
        )}

        {!result && (
          <div className="card p-4 text-sm text-muted-foreground">
            Fyll ut vern, belastning, tverrsnitt, lengde og spenning.
          </div>
        )}
      </div>
    </>
  );
}
